(() => {
  const COOKIE_NAME = 'mollecule_rep';
  const ATTR_KEY = 'rep';
  const MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
  const REP_PATTERN = /^[a-z0-9_-]{1,32}$/;

  /**
   * @param {string | null | undefined} value
   * @returns {string | null}
   */
  function normalizeRep(value) {
    if (!value) return null;
    const normalized = String(value).trim().toLowerCase();
    return REP_PATTERN.test(normalized) ? normalized : null;
  }

  /**
   * @param {string} name
   * @returns {string | null}
   */
  function getCookie(name) {
    const prefix = `${name}=`;
    const match = document.cookie.split('; ').find((entry) => entry.startsWith(prefix));
    if (!match) return null;
    return normalizeRep(decodeURIComponent(match.slice(prefix.length)));
  }

  /**
   * @param {string} name
   * @param {string} value
   */
  function setCookie(name, value) {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
  }

  /**
   * @returns {string | null}
   */
  function readRepFromUrl() {
    try {
      return normalizeRep(new URLSearchParams(location.search).get('rep'));
    } catch {
      return null;
    }
  }

  /**
   * Theme is declared as a top-level const in scripts.liquid (classic script).
   * @returns {{ routes?: { cart_url?: string; cart_update_url?: string } } | undefined}
   */
  function getTheme() {
    try {
      return typeof Theme !== 'undefined' ? Theme : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * @returns {string}
   */
  function cartJsUrl() {
    const cartUrl = getTheme()?.routes?.cart_url || '/cart';
    return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
  }

  /**
   * @returns {string}
   */
  function cartUpdateUrl() {
    const updateUrl = getTheme()?.routes?.cart_update_url || '/cart/update.js';
    return updateUrl.endsWith('.js') ? updateUrl : `${updateUrl}.js`;
  }

  /**
   * @returns {Promise<Record<string, string> | null>}
   */
  async function getCartAttributes() {
    const response = await fetch(cartJsUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return null;
    const cart = await response.json();
    return cart?.attributes && typeof cart.attributes === 'object' ? cart.attributes : {};
  }

  /**
   * @param {string} rep
   */
  async function setCartRep(rep) {
    await fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        attributes: {
          [ATTR_KEY]: rep,
        },
      }),
    });
  }

  /**
   * First-touch: URL sets the cookie only when none exists. Cookie value is then
   * written to the cart so Shopify copies it onto the order at checkout.
   */
  async function syncRepAttribution() {
    const fromUrl = readRepFromUrl();
    let rep = getCookie(COOKIE_NAME);

    if (fromUrl && !rep) {
      setCookie(COOKIE_NAME, fromUrl);
      rep = fromUrl;
    }

    if (!rep) return;

    try {
      const attributes = await getCartAttributes();
      if (!attributes) return;
      if (attributes[ATTR_KEY] === rep) return;
      await setCartRep(rep);
    } catch {
      // Network or privacy-restricted contexts can fail; fail quietly.
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncRepAttribution, { once: true });
  } else {
    syncRepAttribution();
  }
})();
